import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Upload, Github, Globe, Code, FileText, CheckCircle, XCircle, AlertCircle, Clock, Send, RefreshCw, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubmissionPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [team, setTeam] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);

  const [formData, setFormData] = useState({
    github_url: '',
    project_title: '',
    project_description: '',
    tech_stack: [],
    demo_url: ''
  });
  const [techInput, setTechInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    // Set default auth header for axios
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchSubmission();
  }, [token]);

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API}/submissions/my-submission`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmission(response.data.submission);
      setTeam(response.data.team);
      setDeadline(response.data.deadline);
      setCanSubmit(response.data.can_submit);

      if (response.data.submission) {
        setFormData({
          github_url: response.data.submission.github_url || '',
          project_title: response.data.submission.project_title || '',
          project_description: response.data.submission.project_description || '',
          tech_stack: response.data.submission.tech_stack || [],
          demo_url: response.data.submission.demo_url || ''
        });
      }
    } catch (err) {
      console.error('Error fetching submission:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_title || !formData.project_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project submitted successfully!');
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', selectedFile);

    setUploading(true);
    try {
      await axios.post(`${API}/submissions/upload-file`, uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('File uploaded successfully!');
      setSelectedFile(null);
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const addTechStack = () => {
    if (techInput.trim() && !formData.tech_stack.includes(techInput.trim())) {
      setFormData({
        ...formData,
        tech_stack: [...formData.tech_stack, techInput.trim()]
      });
      setTechInput('');
    }
  };

  const removeTechStack = (tech) => {
    setFormData({
      ...formData,
      tech_stack: formData.tech_stack.filter(t => t !== tech)
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1"><Clock size={14} /> Pending Review</span>;
      case 'qualified':
        return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1"><CheckCircle size={14} /> Qualified</span>;
      case 'disqualified':
        return <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1"><XCircle size={14} /> Disqualified</span>;
      default:
        return <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-yellow-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-gray-800 mb-2">No Team Found</h2>
          <p className="text-gray-600 mb-4">You need to be a team leader to submit a project.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold">Project Submission</h1>
              <p className="text-red-200 mt-1">Team: {team.team_name}</p>
            </div>
            {submission && getStatusBadge(submission.status)}
          </div>
          {deadline && (
            <div className="mt-4 bg-white/10 rounded-lg p-3">
              <p className="text-sm">
                <Clock className="inline mr-2" size={16} />
                Deadline: {new Date(deadline).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Submission Status & Feedback */}
        {submission && submission.feedback && submission.feedback.length > 0 && (
          <div className={`rounded-xl p-6 mb-8 ${
            submission.status === 'qualified' ? 'bg-green-50 border-2 border-green-200' :
            submission.status === 'disqualified' ? 'bg-red-50 border-2 border-red-200' :
            'bg-yellow-50 border-2 border-yellow-200'
          }`}>
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              {submission.status === 'qualified' ? <CheckCircle className="text-green-600" /> :
               submission.status === 'disqualified' ? <XCircle className="text-red-600" /> :
               <Clock className="text-yellow-600" />}
              Evaluation Feedback
            </h3>
            <div className="space-y-1">
              {submission.feedback.map((fb, idx) => (
                <p key={idx} className="text-sm">{fb}</p>
              ))}
            </div>
            {submission.status === 'disqualified' && (
              <div className="mt-4 bg-red-100 rounded-lg p-3">
                <p className="text-sm text-red-800 font-semibold">
                  <XCircle className="inline mr-2" size={16} />
                  Your submission did not meet the requirements. Resubmission is not allowed.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Jury Score (if qualified and scored) */}
        {submission && submission.status === 'qualified' && submission.jury_final_score > 0 && (
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Trophy className="text-purple-600" />
              Jury Evaluation
            </h3>
            <div className="flex items-center gap-4">
              <div className="bg-purple-600 text-white text-3xl font-bold px-6 py-3 rounded-xl">
                {submission.jury_final_score}
              </div>
              <div>
                <p className="text-purple-800 font-semibold">Final Score (out of 100)</p>
                <p className="text-purple-600 text-sm">{submission.jury_scores?.length || 0} jury members scored</p>
              </div>
            </div>
            {submission.final_rank && (
              <div className="mt-4 bg-purple-100 rounded-lg p-3">
                <p className="text-purple-800 font-bold text-lg">
                  🏆 Current Rank: #{submission.final_rank}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Submission Form */}
        {canSubmit && (submission?.status !== 'qualified' || !submission) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <FileText className="text-red-600" />
              {submission ? 'Update Submission' : 'Submit Your Project'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.project_title}
                  onChange={(e) => setFormData({ ...formData, project_title: e.target.value })}
                  placeholder="e.g., AI-Powered Healthcare Assistant"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.project_description}
                  onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                  placeholder="Describe your project, its features, and the problem it solves..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200 resize-none"
                  rows={5}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Github className="inline mr-2" size={16} />
                  GitHub Repository URL
                </label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  placeholder="https://github.com/username/project"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Globe className="inline mr-2" size={16} />
                  Demo URL (optional)
                </label>
                <input
                  type="url"
                  value={formData.demo_url}
                  onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                  placeholder="https://your-demo.vercel.app"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Code className="inline mr-2" size={16} />
                  Tech Stack
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTechStack())}
                    placeholder="e.g., React, Node.js, MongoDB"
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500"
                  />
                  <button
                    type="button"
                    onClick={addTechStack}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tech_stack.map((tech, idx) => (
                    <span key={idx} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                      {tech}
                      <button type="button" onClick={() => removeTechStack(tech)} className="hover:text-red-900">×</button>
                    </span>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? 'Submitting...' : (
                  <>
                    <Send size={20} />
                    {submission ? 'Update Submission' : 'Submit Project'}
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* File Upload Section */}
        {canSubmit && submission && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Upload className="text-red-600" />
              Upload Project Files (ZIP)
            </h2>

            {submission.file_uploaded ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-700 flex items-center gap-2">
                  <CheckCircle size={20} />
                  File uploaded: {submission.file_name}
                </p>
              </div>
            ) : null}

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".zip"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-gray-600">Click to select a ZIP file</p>
                <p className="text-gray-400 text-sm">Max size: 100MB</p>
              </label>
              {selectedFile && (
                <p className="mt-2 text-red-600 font-semibold">{selectedFile.name}</p>
              )}
            </div>

            {selectedFile && (
              <button
                onClick={handleFileUpload}
                disabled={uploading}
                className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            )}
          </div>
        )}

        {/* Not Paid Warning */}
        {!canSubmit && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center">
            <AlertCircle className="mx-auto text-yellow-500 mb-4" size={48} />
            <h3 className="text-lg font-bold text-yellow-800 mb-2">Payment Required</h3>
            <p className="text-yellow-700">Complete your hackathon registration payment to submit your project.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionPage;
